import type Phaser from "phaser";
import { JUMP_SHADOW_WIDEST, NPC_SCALES, flightShadowWidth, jumpShadowHeight, jumpShadowWidth } from "odyssey-shared";
import { paintTexture } from "../world/projectile-view.ts";

/**
 * The ground shadow under a hopping character (ADR-116's third amendment of 2026-10-08): a black ellipse at the
 * projectile shadow's strength on the feet line, narrower the higher the character is drawn, hidden on the ground.
 * One texture is painted per width asked for, the first time a character is drawn at it, so a frame only picks one.
 */

/**
 * The shadow's alpha: a projectile's shadow's (`world/projectile-view.ts`), so every shadow on the ground reads alike.
 */
const JUMP_SHADOW_ALPHA = 0.35;

/**
 * The widest shadow painted, in world pixels: the flight's on the ground at the largest look scale an NPC can have, or
 * the hop's widest, whichever is wider.
 */
export const SHADOW_WIDEST = Math.max(JUMP_SHADOW_WIDEST, flightShadowWidth(0, Math.max(...NPC_SCALES)));

/**
 * The narrowest shadow painted, in world pixels.
 */
const SHADOW_NARROWEST = 3;

/**
 * The texture key of the shadow of each width, by width; made once, so a frame looks one up and builds none.
 */
export const SHADOW_KEYS: readonly string[] = Array.from({ length: SHADOW_WIDEST + 1 }, (_, width) => `jump:shadow:${width}`);

/**
 * Paints the shadow of every odd width from the narrowest to the widest, each once per texture manager, when a
 * character's shadow is first made, so no frame paints one.
 *
 * @param textures The texture manager.
 */
function paintShadows(textures: Phaser.Textures.TextureManager): void {
  for (let width = SHADOW_NARROWEST; width <= SHADOW_WIDEST; width += 2) {
    const key = SHADOW_KEYS[width] as string;
    if (textures.exists(key)) {
      continue;
    }
    const height = jumpShadowHeight(width);
    paintTexture(textures, key, width, height, "#000000", (x, y) => (((x + 0.5 - width / 2) / (width / 2)) ** 2 + ((y + 0.5 - height / 2) / (height / 2)) ** 2 <= 1 ? 1 : 0));
  }
}

/**
 * One character's ground shadow. Its image is made the first time the character is drawn lifted, so a character that
 * never hops costs nothing, and placing it allocates nothing after that. It takes the character's depth and is put
 * just below the character's own object in the display list they share, so it draws under the character and, sorting by the
 * same true feet, under whatever covers the character's feet and over whatever the character stands in front of.
 */
export class JumpShadow {
  private image: Phaser.GameObjects.Image | null = null;
  private width = 0;

  /**
   * @param scene The scene the character is drawn in.
   * @param below The character's object (its container or its one sprite), which the shadow is kept just below in the
   *   display list they share.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly below: Phaser.GameObjects.GameObject,
  ) {}

  /**
   * Places the shadow under a character's feet for its lift, or hides it on the ground. Its middle column is the pixel
   * left of the feet point, where the look sheets' standing feet are centred, and its middle row the feet line. Its
   * depth follows the character's on every call, hidden or not: the list's sort is stable, so a shadow left at an old
   * depth while the character walked north would come after it in the list and draw over it at the next hop.
   *
   * @param x The feet across, in whole world pixels.
   * @param y The feet line, in whole world pixels.
   * @param lift How far the character is drawn above its feet, in whole world pixels.
   * @param depth The character's depth, the sort of its true feet.
   */
  public place(x: number, y: number, lift: number, depth: number): void {
    this.placeWidth(x, y, jumpShadowWidth(lift), depth);
  }

  /**
   * Places the shadow under a character's feet at a width the caller chose, or hides it for a width of 0: what a
   * figure whose art stands above its feet by its own measure (a flying one, drawn at its look's scale) draws by. The
   * same footing and depth as `place`.
   *
   * @param x The feet across, in whole world pixels.
   * @param y The feet line, in whole world pixels.
   * @param width The shadow's width in world pixels, odd from the narrowest to the widest painted, or 0 for none.
   * @param depth The character's depth, the sort of its true feet.
   */
  public placeWidth(x: number, y: number, width: number, depth: number): void {
    const held = this.image;
    if (width === 0) {
      if (held !== null) {
        held.setDepth(depth);
        if (held.visible) {
          held.setVisible(false);
        }
      }
      return;
    }
    const image = held ?? this.make();
    if (width !== this.width) {
      image.setTexture(SHADOW_KEYS[width] as string);
      this.width = width;
    }
    image.setPosition(x - 1 - (width - 1) / 2, y - Math.ceil(jumpShadowHeight(width) / 2));
    image.setDepth(depth);
    image.setVisible(true);
  }

  /**
   * Whether the shadow shows now, and how wide, for tests and captures.
   *
   * @returns Its width in world pixels, 0 while hidden.
   */
  public get shown(): number {
    return this.image?.visible === true ? this.width : 0;
  }

  /**
   * Makes the shadow's image, painting the shadows' textures first if the scene has none yet, and puts it just below the
   * character in the list both stand in (the world layer the scene's factory adds to): the list's depth sort is stable,
   * so at the character's depth it stays there.
   *
   * @returns The image, hidden, its origin its top left corner.
   */
  private make(): Phaser.GameObjects.Image {
    paintShadows(this.scene.textures);
    const image = this.scene.add.image(0, 0, SHADOW_KEYS[JUMP_SHADOW_WIDEST] as string).setOrigin(0, 0).setAlpha(JUMP_SHADOW_ALPHA).setVisible(false);
    const list = this.below.displayList;
    if (list !== null && image.displayList === list) {
      list.moveBelow(image, this.below);
    }
    this.image = image;
    this.width = JUMP_SHADOW_WIDEST;
    return image;
  }

  /**
   * Removes the shadow from the scene.
   */
  public destroy(): void {
    this.image?.destroy();
    this.image = null;
  }
}
